/*
 MQTTClient.ts
 =========

------------------------------------------------------------------------
 Author:   Oskar Truffer                                        2025
 Modifs:

 Goal:   MQTT client for FUOTA status tracking & management for TRACE.

   (c) 2025  Oskar Truffer   
   --------------------------

   CSEM S.A.
   Jaquet-Droz 1
   CH-2000 Neuchâtel
   http://www.csem.ch


   THIS SOFTWARE IS CONFIDENTIAL AND CANNOT BE DISTRIBUTED
   WITHOUT THE CSEM PRIOR WRITTEN AGREEMENT.

   THIS  SOFTWARE CAN ONLY BE USED IN THE EUROPEAN PROJECT TRACE AND
   SHALL REMAIN PRIVATE AT ALL TIMES.

------------------------------------------------------------------------
*/
import { IoT } from "@aws-sdk/client-iot";
import { mqtt5, iot } from "aws-iot-device-sdk-v2";
import awsConfig from '../aws-config.json';
import { Credentials } from "./Credentials";
import { interfaces } from "inversify";

/**
 * AWSCognitoCredentialsProvider. The AWSCognitoCredentialsProvider implements AWS.CognitoIdentityCredentials.
 *
 */
class AWSCognitoCredentialsProvider {
    private readonly credentials: Credentials;
    private cachedCredentials?: any;

    constructor(credentials: Credentials, expire_interval_in_ms?: number) {
        this.credentials = credentials;
        setInterval(async () => {
            await this.refreshCredentials();
        }, expire_interval_in_ms ?? 3 * 60 * 1000);
    }

    getCredentials() {
        return {
            aws_access_id: this.cachedCredentials?.accessKeyId ?? "",
            aws_secret_key: this.cachedCredentials?.secretAccessKey ?? "",
            aws_sts_token: this.cachedCredentials?.sessionToken,
            aws_region: awsConfig.aws_cognito_region
        }
    }

    async refreshCredentials() {
        this.cachedCredentials = await this.credentials.get()
    }
}

export async function createClient(context: interfaces.Context): Promise<mqtt5.Mqtt5Client> {
    return new Promise(async (resolve, reject) => {
        const credentials = context.container.get(Credentials);
        console.log(credentials);
    
        const iotClient = new IoT({
            region: awsConfig.aws_cognito_region,
            credentialDefaultProvider: (await credentials.getProvider()) as any
        });
    
        const provider = new AWSCognitoCredentialsProvider(credentials);
        /** Make sure the credential provider fetched before setup the connection */
        await provider.refreshCredentials();
    
        let wsConfig: iot.WebsocketSigv4Config = {
            credentialsProvider: provider,
            region: awsConfig.aws_cognito_region
        }
        const endpoint = await iotClient.describeEndpoint({endpointType: 'iot:Data-ATS'}, {});
        let builder: iot.AwsIotMqtt5ClientConfigBuilder = iot.AwsIotMqtt5ClientConfigBuilder.newWebsocketMqttBuilderWithSigv4Auth(
            endpoint.endpointAddress!,
            wsConfig,
        );
    
        let client: mqtt5.Mqtt5Client = new mqtt5.Mqtt5Client(builder.build());
        let log = console.log;
    
        client.on('error', (error) => {
            reject(error);
        });
    
    
        client.on('attemptingConnect', (eventData: mqtt5.AttemptingConnectEvent) => {
            log("Attempting Connect event");
        });
    
        client.on('connectionSuccess', (eventData: mqtt5.ConnectionSuccessEvent) => {
            log("Connection Success event");
            log("Connack: " + JSON.stringify(eventData.connack));
            log("Settings: " + JSON.stringify(eventData.settings));
            resolve(client);
        });
    
        client.on('connectionFailure', (eventData: mqtt5.ConnectionFailureEvent) => {
            log("Connection failure event: " + eventData.error.toString());
            log(eventData);
            reject(eventData.error);
        });
    
        client.on('disconnection', (eventData: mqtt5.DisconnectionEvent) => {
            log("Disconnection event: " + eventData.error.toString());
            if (eventData.disconnect !== undefined) {
                log('Disconnect packet: ' + JSON.stringify(eventData.disconnect));
            }
        });
    
        client.on('stopped', (eventData: mqtt5.StoppedEvent) => {
            log("Stopped event");
        });

        client.start();
    });
}